/**
 * Main App Component
 * Now uses React Router for navigation
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { loadVendorFromFirestore } from '@/store/thunks/vendorThunks';
import AppRoutes from './routes';

/**
 * SessionLoader runs once after login or page refresh to hydrate all Redux slices
 * from Firestore (the single source of truth).
 *
 * - On fresh login: navigates to the last saved step (returning user) or does
 *   nothing (new user — OTPLoginPage already navigated to /welcome).
 * - On page refresh: just hydrates state, stays on the current URL.
 */
const SessionLoader: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, vendorId } = useAppSelector((s) => s.auth);
  const hasLoaded = useRef(false);

  // Reset the guard so re-login after logout re-runs the thunk
  useEffect(() => {
    if (!isAuthenticated) {
      hasLoaded.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !vendorId || hasLoaded.current) return;
    hasLoaded.current = true;

    // Capture current path synchronously before any async work
    const pathAtLoad = window.location.pathname;

    dispatch(loadVendorFromFirestore(vendorId))
      .unwrap()
      .then((stepPath) => {
        // Only redirect away from entry / unauthenticated routes
        const isEntryRoute = ['/', '/login', '/welcome'].includes(pathAtLoad);
        if (stepPath && isEntryRoute) {
          navigate(stepPath, { replace: true });
        }
      })
      .catch(() => {
        // Firestore data is not critical — app continues without it
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, vendorId]);

  return null;
};

const App: React.FC = () => {
  return (
    <>
      <SessionLoader />
      <AppRoutes />
    </>
  );
};

export default App;